import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { queryClient } from '@/lib/queryClient';
import { useAuth } from './useAuth';
import type { MemoryWithChildren } from './useMemories';
import type { Child } from '@/lib/supabase';
import { ageInMonths } from './useSparkQuestion';
import { getCurrentSeason, type ContentLibraryItem } from './useContentLibrary';
import type { FeedCard } from '@/components/cards/types';
import { fireEvent, onEvent } from '@/lib/events';
import { useProfile } from './queries/useProfile';
import {
  useFeedCategoryWeights,
  useContentLibraryActive,
  useDismissedContent,
  useCardSnoozes,
  useFeedInteractions,
  usePinnedInstances,
  useOwnedSpace,
  useCollectionStepIndex,
  useHasCoParent,
  startOfWeekStr,
  type CategoryWeight,
  type CollectionStepIndex,
  type InteractionRow,
} from './queries/useFeedEngineData';

// ── Cache key + equivalence helpers (Phase 2) ─────────────────────────────────
// `feedCacheKey` is the only place the cache key shape lives.
const feedCacheKey = (userId: string) => ['home-feed', userId] as const;

function cardSig(c: FeedCard): string {
  switch (c.type) {
    case 'streak':       return 'streak';
    case 'memory':       return `mem:${c.data.id}`;
    case 'game':
    case 'tip':
    case 'milestone':    return `${c.type}:${c.data._feedKey ?? c.data.id}`;
    case 'spark':        return 'spark';
    case 'collection':   return `col:${c.data.collectionId}`;
    case 'mori_message': return `mori:${c.data.instanceId}`;
  }
}

// Cheap "is this feed effectively the same?" check — gates cache writes so
// `Math.random()` reshuffles in `assembleBatch` don't churn the UI when input
// data refetches but is structurally unchanged.
function feedsEquivalent(a: FeedCard[], b: FeedCard[]): boolean {
  if (a.length !== b.length) return false;
  const sample = Math.min(3, a.length);
  for (let i = 0; i < sample; i++) {
    if (cardSig(a[i]) !== cardSig(b[i])) return false;
  }
  return true;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface CompletionRow {
  content_id: string;
  space_id: string | null;
  child_id: string | null;
}

export interface FeedEngineResult {
  cards: FeedCard[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  trackCardInteraction: (contentId: string, type: 'shown' | 'yes_tapped' | 'dismissed' | 'swiped_past') => void;
  dismissCard: (contentId: string, childId?: string | null) => void;
  snoozeCard: (contentId: string, childId: string | null, weeks: 1 | 4) => void;
  loadMore: (currentTrackIds: string[]) => void;
  /** Marks a pinned Mori-message instance as read (updates read_at + fires card.tapped). */
  markPinnedRead: (instanceId: string, contentLibraryId: string) => void;
  /** Dismisses a pinned instance (updates dismissed_at + fires card.dismissed + removes from feed). */
  dismissPinnedInstance: (instanceId: string, contentLibraryId: string) => void;
}

/** Interpolate {{variable}} placeholders using the instance payload. */
function interpolate(text: string | null, payload: Record<string, unknown>): string {
  if (!text) return '';
  return text.replace(/\{\{(\w+)\}\}/g, (_m, key) => String(payload[key] ?? ''));
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function weightedPick<T extends { weight: number }>(pool: T[], count: number): T[] {
  const result: T[] = [];
  const remaining = [...pool];
  for (let i = 0; i < count && remaining.length > 0; i++) {
    const totalWeight = remaining.reduce((s, c) => s + c.weight, 0);
    if (totalWeight <= 0) break;
    let roll = Math.random() * totalWeight;
    let idx = 0;
    for (; idx < remaining.length - 1; idx++) {
      roll -= remaining[idx].weight;
      if (roll <= 0) break;
    }
    result.push(remaining[idx]);
    remaining.splice(idx, 1);
  }
  return result;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

const THIRTY_MINUTES_MS = 30 * 60_000;

function contentFresh(lastShownMs: number | undefined, relaxLevel: number): boolean {
  if (lastShownMs === undefined) return true;
  const elapsed = Date.now() - lastShownMs;
  const shownToday = new Date(lastShownMs).toISOString().split('T')[0] === todayStr();
  if (relaxLevel >= 3) return true;
  if (relaxLevel === 0 && elapsed < THIRTY_MINUTES_MS) return false;
  if (relaxLevel <= 1 && shownToday && elapsed >= THIRTY_MINUTES_MS) return Math.random() < 0.5;
  return true;
}

function memoryFresh(lastShownMs: number | undefined, relaxLevel: number): boolean {
  if (lastShownMs === undefined) return true;
  const elapsed = Date.now() - lastShownMs;
  const shownToday = new Date(lastShownMs).toISOString().split('T')[0] === todayStr();
  const shownThisWeek = new Date(lastShownMs).toISOString().split('T')[0] >= startOfWeekStr();
  if (relaxLevel >= 3) return true;
  if (relaxLevel === 0 && elapsed < THIRTY_MINUTES_MS) return false;
  if (relaxLevel <= 1 && shownToday) return Math.random() < 0.3;
  if (relaxLevel <= 2 && shownThisWeek) return Math.random() < 0.6;
  return true;
}

// ── Feed personalisation ──────────────────────────────────────────────────────

// Returns a weight multiplier in [0.3, 1.5] derived from the user's interaction
// history for a given category. Uses card_type (matches feed_category_weights.id)
// to join content_library items → interactions. Returns 1.0 when there's no signal.
function personalMultiplier(
  categoryId: string,
  interactions: InteractionRow[],
  contentLibrary: ContentLibraryItem[],
): number {
  const catContentIds = new Set(
    contentLibrary.filter((c) => c.card_type === categoryId).map((c) => c.id),
  );
  const catInteractions = interactions.filter((i) => catContentIds.has(i.content_id));
  const accepted = catInteractions.filter((i) => i.interaction_type === 'yes_tapped').length;
  const rejected = catInteractions.filter(
    (i) => i.interaction_type === 'swiped_past' || i.interaction_type === 'dismissed',
  ).length;
  if (accepted === 0 && rejected === 0) return 1.0;
  const ratio = accepted / (accepted + rejected);
  return Math.min(1.5, Math.max(0.3, ratio));
}

// ── Multi-child name resolution ──────────────────────────────────────────────

/**
 * Resolves [name] for a content card based on subject:
 *   subject='specific_child' → youngest age-matched child; youngest overall as fallback
 *   subject='any_child'      → "Leo" (1 child), "Leo or Mia" (2), "your child" (3+/0)
 */
function resolveChildName(
  item: ContentLibraryItem,
  children: Child[],
): string {
  if (children.length === 0) return 'your child';

  if (item.subject === 'specific_child') {
    const min = item.age_min_months ?? 0;
    const max = item.age_max_months ?? 9999;
    // Find children in the age range, sorted youngest-first
    const matching = children
      .filter((c) => {
        const age = ageInMonths(c.birthdate);
        return age >= min && age <= max;
      })
      .sort((a, b) => ageInMonths(a.birthdate) - ageInMonths(b.birthdate));

    if (matching.length > 0) return matching[0].name;
    // No age match — pick the youngest child overall
    return children.reduce((a, b) =>
      ageInMonths(a.birthdate) < ageInMonths(b.birthdate) ? a : b,
    ).name;
  }

  // any_child: multi-child name
  if (children.length === 1) return children[0].name;
  if (children.length === 2) return `${children[0].name} or ${children[1].name}`;
  return 'your child';
}

/** Replace [name] / [tribeName] placeholders in a content item based on subject. */
function personaliseItem(
  item: ContentLibraryItem,
  children: Child[],
  spaceName: string,
): ContentLibraryItem {
  if (item.subject !== 'specific_child' && item.subject !== 'any_child') {
    // TODO: remove after card_target drop — tribe rows now have subject='parent_only';
    // keep reading card_target here for [tribeName] substitution until card_target is dropped.
    if (item.card_target === 'tribe') {
      const tribeName = spaceName || 'your family';
      return {
        ...item,
        title: item.title?.replace(/\[tribeName\]/g, tribeName) ?? null,
        body: item.body.replace(/\[tribeName\]/g, tribeName),
      };
    }
    // parent_only / non-tribe: no placeholder substitution
    return item;
  }

  // specific_child uses _resolvedChildName set by expandCard; any_child falls back to resolveChildName
  const name = item._resolvedChildName ?? resolveChildName(item, children);
  return {
    ...item,
    title: item.title?.replace(/\[name\]/g, name) ?? null,
    body: item.body.replace(/\[name\]/g, name),
  };
}

/** Check if at least one child's age falls within the item's range. */
function anyChildInAgeRange(item: ContentLibraryItem, children: Child[]): boolean {
  if (children.length === 0) return item.age_min_months == null; // no children → only ageless cards
  const min = item.age_min_months ?? 0;
  const max = item.age_max_months ?? 9999;
  return children.some((c) => {
    const age = ageInMonths(c.birthdate);
    return age >= min && age <= max;
  });
}

const BATCH_SIZE = 10;

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useFeedEngine(
  sessionId: string,
  accountCreatedAt: string,
  memories: MemoryWithChildren[],
  memoriesLoading: boolean,
  children: Child[],
  childrenLoading: boolean,
): FeedEngineResult {
  const { user } = useAuth();

  // Hydrate from the persisted TanStack cache so warm opens render in the same
  // frame as mount — no skeleton flash. Empty array (`[]`) is treated as a
  // known-empty result, not a loading state.
  const [cards, setCards] = useState<FeedCard[]>(() => {
    const uid = user?.id;
    if (!uid) return [];
    return queryClient.getQueryData<FeedCard[]>(feedCacheKey(uid)) ?? [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    const uid = user?.id;
    if (!uid) return true;
    return queryClient.getQueryData<FeedCard[]>(feedCacheKey(uid)) === undefined;
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // ── Cached data ──────────────────────────────────────────────────────────
  const weightsRef = useRef<CategoryWeight[]>([]);
  const contentPoolRef = useRef<ContentLibraryItem[]>([]); // pre-filtered by age/gender/season, RAW (no [name] replacement)
  const allContentRef = useRef<ContentLibraryItem[]>([]); // raw unfiltered — used to rebuild pool when children change
  const interactionsRef = useRef<InteractionRow[]>([]); // 30-day interaction history for personalisation
  const childrenRef = useRef<Child[]>([]); // cached for name resolution in assembleBatch
  const dismissedRef = useRef<{ content_id: string; child_id: string | null }[]>([]);
  const snoozedRef = useRef<{ content_id: string; child_id: string | null }[]>([]);
  const sessionDismissedRef = useRef<Set<string>>(new Set());
  const mutedTypesRef = useRef<string[]>([]);
  const completionsRef = useRef<CompletionRow[]>([]);
  const spaceNameRef = useRef<string>('');
  const activeSpaceIdRef = useRef<string | null>(null);
  const hasCoParentRef = useRef<boolean>(false);
  const contentShownRef = useRef<Map<string, number>>(new Map());
  const memoryShownRef = useRef<Map<string, number>>(new Map());
  const accountAgeDaysRef = useRef(0);
  const isOnboardingRef = useRef(false);
  const feedDataReadyRef = useRef(false);
  const [feedDataTick, setFeedDataTick] = useState(0); // bumped when eager fetch completes
  // Pinned (pin_to_top) Mori-message instances, already interpolated + filtered.
  const pinnedCardsRef = useRef<FeedCard[]>([]);
  const collectionStepIndexRef = useRef<{
    stepIndex: CollectionStepIndex;
    boostedContentIds: Set<string>;
    startedTemplateIds: Set<string>;
    firstStepByTemplate: Map<string, string>;
  } | null>(null);

  // ── TanStack Query data hooks ─────────────────────────────────────────────
  const { data: weightsData, isLoading: weightsLoading } = useFeedCategoryWeights();
  const { data: contentData, isLoading: contentLoading } = useContentLibraryActive();
  const { data: dismissedData, isLoading: dismissedLoading } = useDismissedContent(user?.id);
  const { data: snoozedData, isLoading: snoozedLoading } = useCardSnoozes(user?.id);
  const { data: interactionsData, isLoading: interactionsLoading } = useFeedInteractions(user?.id);
  const { data: pinnedData, isLoading: pinnedLoading } = usePinnedInstances(user?.id);
  const { data: ownedSpaceData, isLoading: ownedSpaceLoading } = useOwnedSpace(user?.id);
  const { profile, isLoading: profileLoading } = useProfile();
  const { data: collectionStepIndexData, isLoading: collectionStepIndexLoading } = useCollectionStepIndex(user?.id);
  // Derive activeSpaceId at hook level so useHasCoParent can be called unconditionally.
  // Mirrors the engine's resolution logic (profile.active_space_id → owned space id).
  // The space_members fallback (for old co-parents) runs inside the useEffect below
  // and sets activeSpaceIdRef — that case is safe-by-default (ref starts false).
  const derivedActiveSpaceId: string | null = profile?.active_space_id ?? ownedSpaceData?.id ?? null;
  const { data: hasCoParentData, isLoading: hasCoParentLoading } = useHasCoParent(user?.id ?? null, derivedActiveSpaceId);

  const feedQueriesLoading =
    weightsLoading || contentLoading || dismissedLoading || snoozedLoading ||
    interactionsLoading || pinnedLoading || ownedSpaceLoading || profileLoading ||
    collectionStepIndexLoading || hasCoParentLoading;

  // ── Build a batch of cards from cached data ──────────────────────────────
  const assembleBatch = useCallback((
    excludeIds: Set<string>,
    relaxLevel: number,
    batchMemories: MemoryWithChildren[],
  ): FeedCard[] => {
    const weights = weightsRef.current;
    const pool = contentPoolRef.current;
    const cachedChildren = childrenRef.current;
    const dismissed = dismissedRef.current;
    const snoozed = snoozedRef.current;
    const completions = completionsRef.current;
    const activeSpaceId = activeSpaceIdRef.current;
    const accountAgeDays = accountAgeDaysRef.current;
    const isOnboarding = isOnboardingRef.current;

    const today = todayStr();
    const weekStart = startOfWeekStr();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const sevenDaysAgoStr = sevenDaysAgo.toISOString().split('T')[0];
    const todayShownIds = new Set<string>();
    const weekShownIds = new Set<string>();

    for (const [cid, ts] of contentShownRef.current) {
      const d = new Date(ts).toISOString().split('T')[0];
      if (d === today) todayShownIds.add(cid);
      if (d >= weekStart) weekShownIds.add(cid);
    }

    const eligible = pool.filter((item) => {
      // Viewer scoping: co_parent cards are hidden for solo parents.
      if (item.viewer === 'co_parent' && !hasCoParentRef.current) return false;

      // Child cards: only a global (child_id=null) dismiss/snooze removes from pool;
      // per-child dismissals are handled inside expandCard.
      if (item.card_target === 'child') {
        if (dismissed.some((d) => d.content_id === item.id && d.child_id === null)) return false;
        if (snoozed.some((s) => s.content_id === item.id && s.child_id === null)) return false;
      } else {
        if (dismissed.some((d) => d.content_id === item.id)) return false;
        if (snoozed.some((s) => s.content_id === item.id)) return false;
      }
      if (sessionDismissedRef.current.has(item.id)) return false;
      if (mutedTypesRef.current.includes(item.card_type)) return false;
      if (excludeIds.has(item.id)) return false;
      if (!contentFresh(contentShownRef.current.get(item.id), relaxLevel)) return false;

      if (relaxLevel === 0) {
        switch (item.frequency) {
          case 'always': break;
          case 'daily': if (todayShownIds.has(item.id)) return false; break;
          case 'weekly': if (weekShownIds.has(item.id)) return false; break;
          case 'occasional': if (Math.random() >= 0.33) return false; break;
          case 'rare': if (Math.random() >= 0.10) return false; break;
        }
      }

      return true;
    });

    // Badge annotation + priority boost (combined to avoid two passes over eligible).
    // _collectionContext is attached when the item is a collection template step.
    // stepTitleOverride replaces title when set. boostedContentIds items get 2× weight
    // (at most 3 boosts total — guaranteed by fetchCollectionStepIndex cap).
    const csIndex = collectionStepIndexRef.current;

    // Compute once before the category loop — cards the user tapped in the last
    // 7 days but hasn't completed yet get a 1.3× weight boost.
    const recentInteractions = interactionsRef.current.filter(
      (i) => i.session_date >= sevenDaysAgoStr,
    );
    const recentTappedIds = new Set(
      recentInteractions
        .filter((i) => i.interaction_type === 'yes_tapped')
        .map((i) => i.content_id),
    );
    const recentCompletedIds = new Set(
      recentInteractions
        .filter((i) => i.interaction_type === 'challenge_completed')
        .map((i) => i.content_id),
    );
    const recentlyTapped = new Set(
      [...recentTappedIds].filter((id) => !recentCompletedIds.has(id)),
    );

    const boosted = eligible.map((item) => {
      const stepMeta = csIndex?.stepIndex.get(item.id);
      const baseWeight = accountAgeDays <= item.priority_days ? item.weight * 3 : item.weight;
      const isBoosted = csIndex?.boostedContentIds.has(item.id) ?? false;
      const collectionWeight = isBoosted ? baseWeight * 2.0 : baseWeight;
      const cardBoost = recentlyTapped.has(item.id) ? 1.3 : 1.0;
      return {
        ...item,
        weight: collectionWeight * cardBoost,
        ...(stepMeta ? {
          _collectionContext: stepMeta,
          title: stepMeta.stepTitleOverride ?? item.title,
        } : {}),
      };
    });

    const byCategory: Record<string, typeof boosted> = {};
    for (const item of boosted) {
      if (!byCategory[item.card_type]) byCategory[item.card_type] = [];
      byCategory[item.card_type].push(item);
    }

    // ── Batch-local helpers ──────────────────────────────────────────────────

    function isDismissed(contentId: string, childId?: string | null): boolean {
      return dismissed.some((d) => {
        if (d.content_id !== contentId) return false;
        if (d.child_id === null) return true;               // global dismiss
        if (childId != null && d.child_id === childId) return true; // child-specific
        return false;
      });
    }

    function isCompleted(
      contentLibraryId: string,
      childId?: string | null,
      isTribe?: boolean,
    ): boolean {
      return completions.some((c) => {
        if (c.content_id !== contentLibraryId) return false;
        if (isTribe) return c.space_id === activeSpaceId;
        if (childId != null) return c.child_id === childId;
        return c.child_id === null && c.space_id === null;
      });
    }

    function expandCard(item: ContentLibraryItem): ContentLibraryItem[] {
      // parent_only: single copy, check global dismiss + completion
      if (item.subject === 'parent_only') {
        if (isDismissed(item.id)) return [];
        // TODO: remove after card_target drop — tribe rows have subject='parent_only';
        // keep reading card_target here to detect tribe completions until card_target is dropped.
        if (isCompleted(item.id, null, item.card_target === 'tribe')) return [];
        return [{ ...item, _feedKey: item.id }];
      }

      // any_child: single card — user picks child at capture, no per-child expansion
      if (item.subject === 'any_child') {
        if (isDismissed(item.id)) return [];
        if (isCompleted(item.id)) return [];
        return [{ ...item, _feedKey: item.id }];
      }

      // specific_child: resolve age-matched children (youngest-first), fallback to youngest overall
      const min = item.age_min_months ?? 0;
      const max = item.age_max_months ?? 9999;
      const inRange = cachedChildren.filter((c) => {
        const age = ageInMonths(c.birthdate);
        return age >= min && age <= max;
      });

      const targets = (inRange.length > 0
        ? inRange
        : cachedChildren.length > 0
          ? [cachedChildren.reduce((a, b) =>
              ageInMonths(a.birthdate) < ageInMonths(b.birthdate) ? a : b,
            )]
          : []
      ).sort((a, b) => ageInMonths(a.birthdate) - ageInMonths(b.birthdate));

      if (targets.length === 0) return [];

      // user_selects: single card with pre-assigned youngest matched child for name display
      if (item.child_assignment === 'user_selects') {
        const child = targets[0];
        if (isDismissed(item.id, child.id) || isCompleted(item.id, child.id)) return [];
        return [{
          ...item,
          _resolvedChildId: child.id,
          _resolvedChildName: child.name,
          _feedKey: item.id,
        }];
      }

      // expand: one card per age-matched child, capped at MAX_COPIES_PER_CARD (youngest first)
      const MAX_COPIES_PER_CARD = 2;
      return targets
        .slice(0, MAX_COPIES_PER_CARD)
        .filter((child) => !isDismissed(item.id, child.id) && !isCompleted(item.id, child.id))
        .map((child) => ({
          ...item,
          _resolvedChildId: child.id,
          _resolvedChildName: child.name,
          _feedKey: `${item.id}:${child.id}`,
        }));
    }

    // ── Category loop ────────────────────────────────────────────────────────

    const result: FeedCard[] = [];

    for (const catWeight of weights) {
      const baseW = isOnboarding ? catWeight.onboarding_weight : catWeight.base_weight;
      const effectiveW = isOnboarding
        ? baseW
        : baseW * personalMultiplier(catWeight.id, interactionsRef.current, allContentRef.current);
      if (Math.random() * 100 > effectiveW) continue;

      if (catWeight.id === 'memory') {
        const eligibleMemories = batchMemories.filter((m) => {
          if (excludeIds.has(m.id)) return false;
          return memoryFresh(memoryShownRef.current.get(m.id), relaxLevel);
        });
        shuffle(eligibleMemories).slice(0, catWeight.max_per_feed)
          .forEach((m) => result.push({ type: 'memory', data: m }));
        continue;
      }

      const candidates = byCategory[catWeight.id] ?? [];
      if (candidates.length === 0) continue;

      // Cap on unique content_library IDs — expansion can produce N copies per ID
      const pickedItems = weightedPick(candidates, catWeight.max_per_feed);
      const uniqueCardIds = new Set<string>();
      const expandedCards: ContentLibraryItem[] = [];
      for (const item of pickedItems) {
        if (uniqueCardIds.size >= catWeight.max_per_feed) break;
        if (uniqueCardIds.has(item.id)) continue;
        uniqueCardIds.add(item.id);
        expandedCards.push(...expandCard(item));
      }
      for (const expandedItem of expandedCards) {
        const personalised = personaliseItem(expandedItem, cachedChildren, spaceNameRef.current);
        result.push({ type: catWeight.id === 'challenge' ? 'game' : 'tip', data: personalised });
      }
    }

    // ── Teaser injection ─────────────────────────────────────────────────────
    // Once per batch: inject a synthetic collection card for one unstarted template.
    // Frequency-gated via card_snoozes — uses the first step's content_library_id
    // as the snooze proxy (Task 4 writes the snooze on "Maybe later" dismiss).
    if (csIndex) {
      const { stepIndex, startedTemplateIds, firstStepByTemplate } = csIndex;
      const seenTemplateIds = new Set<string>();

      for (const meta of stepIndex.values()) {
        if (seenTemplateIds.has(meta.templateId)) continue;
        seenTemplateIds.add(meta.templateId);

        if (startedTemplateIds.has(meta.templateId)) continue;

        const firstStepId = firstStepByTemplate.get(meta.templateId);
        if (firstStepId && snoozed.some((s) => s.content_id === firstStepId && s.child_id === null)) continue;

        result.push({
          type: 'collection' as const,
          data: {
            collectionId: `teaser-${meta.templateId}`,
            collectionName: meta.templateName,
            collectionIcon: meta.templateIcon,
            collectionColor: meta.templateColor,
            entryCount: 0,
            lastEntryAt: null,
            contentLibraryId: firstStepId ?? '',
            cardContext: {
              contentLibraryId: firstStepId ?? '',
              title: meta.templateName,
              body: '',
              captureType: 'voice',
              tags: [],
              cardType: 'collection',
            },
            participantConnectionIds: [],
            participantChildIds: [],
            lockedChildIds: [],
            lockedMemberIds: [],
          },
        });
        break; // one teaser per batch
      }
    }

    return shuffle(result).slice(0, BATCH_SIZE);
  }, []);

  // ── Effect 1: Process TanStack query data into refs when all queries are ready ─
  // Re-runs whenever any cached input dataset changes (TanStack returns a new
  // reference only when data actually differs, thanks to structural sharing).
  // The inline supabase fetches (challenge_completions + joined-space name)
  // are gated behind `feedDataReadyRef` so they fire only on the first run —
  // completions stay fresh via the `challenge.completed` event subscriber.
  useEffect(() => {
    if (!user) return;
    if (feedQueriesLoading) return;

    let cancelled = false;

    (async () => {
      weightsRef.current = weightsData ?? [];
      allContentRef.current = contentData ?? [];

      dismissedRef.current = (dismissedData ?? []).map((d) => ({
        content_id: d.content_id,
        child_id: d.child_id ?? null,
      }));

      snoozedRef.current = (snoozedData ?? []).map((s) => ({
        content_id: s.content_id,
        child_id: s.child_id ?? null,
      }));

      mutedTypesRef.current = profile?.muted_card_types ?? [];
      // Defensive guard: a Map/Set persisted via JSON.stringify rehydrates as
      // a plain object with no .get/.has, which crashed the boost/expand passes
      // before the persister was taught to skip this key. Treat any non-Map
      // shape as "no index" so the next refetch can replace it cleanly.
      collectionStepIndexRef.current =
        collectionStepIndexData &&
        collectionStepIndexData.stepIndex instanceof Map &&
        collectionStepIndexData.boostedContentIds instanceof Set
          ? collectionStepIndexData
          : null;
      hasCoParentRef.current = hasCoParentData ?? false;

      if (!feedDataReadyRef.current) {
        // Resolve active space for space-wide challenge completion fetch.
        // Owners: active_space_id is null → fall back to their owned space.
        // Co-parents: active_space_id is set by accept-coparent-invite → no owned space row.
        // Fallback for existing co-parents who accepted before this was wired: look up
        // their space_members row to find the space they belong to.
        const typedSpace = ownedSpaceData ?? null;
        const profileActiveSpaceId = profile?.active_space_id ?? null;
        let activeSpaceId: string | null = profileActiveSpaceId ?? typedSpace?.id ?? null;

        if (!activeSpaceId) {
          // Fallback: co-parent with no owned space and no active_space_id set yet.
          const { data: membership } = await supabase
            .from('space_members')
            .select('space_id')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .neq('role', 'owner')
            .limit(1)
            .maybeSingle();
          activeSpaceId = (membership as { space_id: string } | null)?.space_id ?? null;
          // Back-fill active_space_id so the fallback isn't needed next time.
          if (activeSpaceId) {
            void supabase
              .from('profiles')
              .update({ active_space_id: activeSpaceId })
              .eq('id', user.id);
          }
        }
        activeSpaceIdRef.current = activeSpaceId;

        if (typedSpace) {
          spaceNameRef.current = typedSpace.name;
        } else if (activeSpaceId) {
          // Co-parent path: fetch the space name from the space they joined.
          const { data: joinedSpace } = await supabase
            .from('spaces')
            .select('name')
            .eq('id', activeSpaceId)
            .maybeSingle();
          spaceNameRef.current = (joinedSpace as { name: string } | null)?.name ?? '';
        } else {
          spaceNameRef.current = '';
        }

        // Fetch challenge completions — include space-wide completions when a space is active
        let completionsQuery = supabase
          .from('challenge_completions')
          .select('content_id, space_id, child_id')
          .eq('user_id', user.id);

        if (activeSpaceId) {
          completionsQuery = supabase
            .from('challenge_completions')
            .select('content_id, space_id, child_id')
            .or(`user_id.eq.${user.id},space_id.eq.${activeSpaceId}`);
        }

        const { data: completionsData } = await completionsQuery;
        if (!cancelled) {
          completionsRef.current = (completionsData as CompletionRow[] | null) ?? [];
        }
      }

      const interactions = interactionsData ?? [];
      interactionsRef.current = interactions;
      for (const row of interactions) {
        if (row.interaction_type === 'shown') {
          const ts = new Date(row.created_at).getTime();
          const prev = contentShownRef.current.get(row.content_id);
          if (!prev || ts > prev) contentShownRef.current.set(row.content_id, ts);
        }
      }

      // Filter + interpolate pinned Mori-message instances.
      // Results are ordered newest-first, so dedup by content_library_id
      // keeps only the most recent instance per template — prevents the same
      // card type from stacking when an event fires multiple times.
      const now = Date.now();
      const seenTemplates = new Set<string>();
      pinnedCardsRef.current = (pinnedData ?? [])
        .filter((row) => {
          const cl = row.content_library;
          if (!cl || !cl.active || !cl.pin_to_top) return false;
          // Respect auto_dismiss_hours — treat older instances as expired
          if (cl.auto_dismiss_hours != null) {
            const ageMs = now - new Date(row.created_at).getTime();
            if (ageMs > cl.auto_dismiss_hours * 3_600_000) return false;
          }
          // One instance per template+child in the feed (newest wins — already sorted).
          // Birthday cards are child-specific: "birthday-id:emma-id" and
          // "birthday-id:tom-id" are distinct keys so both siblings get a card.
          const dedupKey = row.payload?.childId
            ? `${cl.id}:${String(row.payload.childId)}`
            : cl.id;
          if (seenTemplates.has(dedupKey)) return false;
          seenTemplates.add(dedupKey);
          return true;
        })
        .map((row) => {
          const cl = row.content_library!;
          const payload = row.payload ?? {};
          return {
            type: 'mori_message' as const,
            data: {
              instanceId: row.id,
              contentLibraryId: cl.id,
              title: cl.title ? interpolate(cl.title, payload) : null,
              body: interpolate(cl.body, payload),
              ctaAction: cl.cta_action,
              cardType: cl.card_type,
            },
          };
        });

      if (!cancelled) {
        feedDataReadyRef.current = true;
        setFeedDataTick((t) => t + 1); // trigger effect 2
      }
    })();

    return () => { cancelled = true; };
  }, [user, feedQueriesLoading, weightsData, contentData, dismissedData, snoozedData, interactionsData, pinnedData, ownedSpaceData, profile, collectionStepIndexData, hasCoParentData]);

  // ── Effect 2: Assemble — runs when feed data AND memories/children are ready ─
  // Re-runs on every input change so the feed stays in sync with fresh data,
  // but the equivalence check at the bottom skips re-renders when the new feed
  // is structurally the same as what's already cached. That keeps `Math.random`
  // reshuffles inside `assembleBatch` from churning the UI on each refetch.
  useEffect(() => {
    if (!user || memoriesLoading || childrenLoading) return;
    if (!feedDataReadyRef.current) return;

    // Filter content pool: non-child cards bypass child-based filters entirely
    const currentSeason = getCurrentSeason();
    const childGenders = children.map((c) => c.gender ?? 'neutral');

    contentPoolRef.current = allContentRef.current.filter((item) => {
      // parent / tribe / any cards: skip all child-based filters
      if (item.card_target !== 'child') {
        return item.active !== false;
      }

      // child cards: apply age range, gender, and season filters as before
      if (!anyChildInAgeRange(item, children)) return false;
      if (item.genders?.length > 0) {
        const genderMatch = childGenders.some((g) =>
          item.genders.includes(g) || item.genders.includes('neutral'),
        );
        if (!genderMatch) return false;
      }
      if (item.seasons?.length > 0 && !item.seasons.includes(currentSeason)) return false;
      return true;
    });

    childrenRef.current = children;
    accountAgeDaysRef.current = daysSince(accountCreatedAt);
    isOnboardingRef.current = accountAgeDaysRef.current <= 30;


    // Retry with increasing relaxation to guarantee a non-empty feed.
    let firstBatch: FeedCard[] = [];
    for (let relax = 0; relax <= 3; relax++) {
      firstBatch = assembleBatch(new Set(), relax, memories);
      if (firstBatch.length >= 3) break;
    }
    // Streak first (index 0), then pinned Mori-messages, then regular batch.
    const feed: FeedCard[] = [
      { type: 'streak', data: null },
      ...pinnedCardsRef.current,
      ...firstBatch,
    ];

    // Collection-step injection: handled via boostedContentIds from useCollectionStepIndex (capped at 3).

    // Skip re-render if the assembled feed is structurally equivalent to the
    // cached one — avoids pointless reshuffles when refetched data is the same.
    const cached = queryClient.getQueryData<FeedCard[]>(feedCacheKey(user.id));
    if (cached && feedsEquivalent(cached, feed)) {
      if (cards !== cached) setCards(cached);
      if (isLoading) setIsLoading(false);
      return;
    }

    // Cap at 30 entries — older cards regenerate via loadMore (localStorage bloat guard)
    const cappedFeed = feed.slice(0, 30);
    queryClient.setQueryData(feedCacheKey(user.id), cappedFeed);
    setCards(cappedFeed);
    setIsLoading(false);
  // `cards` and `isLoading` are intentionally read but not in deps — they're
  // only consulted to short-circuit redundant setState when the feed is
  // unchanged. Re-running the effect because they change would cause loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, memoriesLoading, childrenLoading, memories, children, accountCreatedAt, assembleBatch, feedDataTick]);

  // ── loadMore ──────────────────────────────────────────────────────────────

  const loadMore = useCallback(
    (currentTrackIds: string[]) => {
      if (isLoadingMore || !hasMore) return;
      setIsLoadingMore(true);

      requestAnimationFrame(() => {
        const excludeSet = new Set(currentTrackIds);
        let batch: FeedCard[] = [];

        for (let relax = 0; relax <= 3; relax++) {
          batch = assembleBatch(excludeSet, relax, memories);
          if (batch.length > 0) break;
        }

        if (batch.length === 0) {
          // Cycle: clear session-level shown history so all cards are fresh,
          // then try one more assembly. Only hard-stop when there's genuinely
          // no content at all (no content library rows, no memories).
          contentShownRef.current.clear();
          memoryShownRef.current.clear();
          for (let relax = 0; relax <= 3; relax++) {
            batch = assembleBatch(new Set(), relax, memories);
            if (batch.length > 0) break;
          }
        }

        if (batch.length === 0) {
          setHasMore(false);
        } else {
          setCards((prev) => [...prev, ...batch]);
        }

        setIsLoadingMore(false);
      });
    },
    [isLoadingMore, hasMore, assembleBatch, memories],
  );

  // ── Phase 3: feed cache invalidation on key lifecycle events ────────────────
  // Keeps the home-feed cache correct without polling or manual refresh.
  useEffect(() => {
    if (!user) return;
    const uid = user.id;
    const key = feedCacheKey(uid);

    const unsubs = [
      // New memory captured → rebuild the feed so the memory card appears.
      onEvent('memory.created', ({ userId }) => {
        if (userId !== uid) return;
        void queryClient.invalidateQueries({ queryKey: key });
      }),
      // Mori message inserted → refresh pinned instances + assembled feed.
      onEvent('mori_message.created', ({ userId }) => {
        if (userId !== uid) return;
        void queryClient.invalidateQueries({ queryKey: ['pinned-instances', uid] });
        void queryClient.invalidateQueries({ queryKey: key });
      }),
      // Tier change → card gating may have changed.
      onEvent('subscription.upgraded', ({ userId }) => {
        if (userId !== uid) return;
        void queryClient.invalidateQueries({ queryKey: key });
      }),
      // Active space changed → cards are space-scoped (member audience filter,
      // challenge completions, space name interpolation), so the cached feed
      // belongs to the previous space and must be discarded.
      onEvent('space.switched', ({ userId }) => {
        if (userId !== uid) return;
        void queryClient.invalidateQueries({ queryKey: key });
      }),
    ];

    return () => unsubs.forEach((fn) => fn());
  }, [user]);

  // ── Phase 3: foreground re-fetch after >60s in background ───────────────────
  const backgroundAtRef = useRef<number>(0);
  useEffect(() => {
    if (!user) return;
    const uid = user.id;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        backgroundAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        if (backgroundAtRef.current > 0 && Date.now() - backgroundAtRef.current > 60_000) {
          void queryClient.invalidateQueries({ queryKey: feedCacheKey(uid) });
        }
        backgroundAtRef.current = 0;
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user]);

  // ── card.shown subscriber — keeps contentShownRef and memoryShownRef fresh ─
  // Replaces the direct ref update that was inline in trackCardInteraction.
  // onEvent is synchronous so the ref update happens in the same tick as fireEvent.

  useEffect(() => {
    return onEvent('card.shown', ({ contentLibraryId }) => {
      contentShownRef.current.set(contentLibraryId, Date.now());
      memoryShownRef.current.set(contentLibraryId, Date.now());
    });
  }, []);

  // ── challenge.completed subscriber — keeps completionsRef fresh mid-session ─
  // Without this, a completed card stays visible until next page load because
  // completionsRef is only populated at session start (Effect 1).
  // The event doesn't carry child_id/space_id, so we push with nulls — this
  // correctly hides personal cards; child/tribe cards clear on next reload.

  useEffect(() => {
    return onEvent('challenge.completed', ({ contentLibraryId }) => {
      completionsRef.current.push({
        content_id: contentLibraryId,
        child_id: null,
        space_id: null,
      });
    });
  }, []);

  // ── Interaction tracking ──────────────────────────────────────────────────
  // trackCardInteraction signature is unchanged — callers are unaffected.
  // DB writes now go through contentInteractionSubscriber via events.

  const trackCardInteraction = useCallback(
    (contentId: string, type: 'shown' | 'yes_tapped' | 'dismissed' | 'swiped_past') => {
      if (!user) return;
      const cardType = contentPoolRef.current.find((c) => c.id === contentId)?.card_type ?? 'unknown';

      if (type === 'shown') {
        fireEvent('card.shown', {
          userId: user.id,
          contentLibraryId: contentId,
          cardType,
          instanceId: null,
        });
      } else if (type === 'yes_tapped') {
        fireEvent('card.tapped', {
          userId: user.id,
          contentLibraryId: contentId,
          cardType,
          ctaAction: 'record',
        });
      } else if (type === 'swiped_past') {
        fireEvent('card.swiped_past', {
          userId: user.id,
          contentLibraryId: contentId,
          cardType,
        });
      } else if (type === 'dismissed') {
        fireEvent('card.dismissed', {
          userId: user.id,
          contentLibraryId: contentId,
          cardType,
          dismissedFor: '3_months',
        });
      }
    },
    [user],
  );

  // ── Dismiss ───────────────────────────────────────────────────────────────

  const dismissCard = useCallback(
    (contentId: string, childId?: string | null) => {
      if (!user) return;
      sessionDismissedRef.current.add(contentId);
      // Same predicate applied to local state and the persisted cache so a
      // background refresh landing right after the dismiss can't bring the
      // card back into view.
      const keep = (c: FeedCard): boolean => {
        if (c.type === 'game' || c.type === 'tip' || c.type === 'milestone') {
          if (c.data.id !== contentId) return true;        // different card — keep
          if (!childId) return false;                       // global dismiss — remove all copies
          return c.data._resolvedChildId !== childId;       // keep other children's copies
        }
        return true;
      };
      setCards((prev) => prev.filter(keep));
      queryClient.setQueryData<FeedCard[]>(feedCacheKey(user.id), (prev) =>
        prev ? prev.filter(keep) : prev,
      );
      const cardType = contentPoolRef.current.find((c) => c.id === contentId)?.card_type ?? 'unknown';
      fireEvent('card.dismissed', {
        userId: user.id,
        contentLibraryId: contentId,
        cardType,
        dismissedFor: '3_months',
      });
    },
    [user],
  );

  const snoozeCard = useCallback(
    (contentId: string, childId: string | null, weeks: 1 | 4) => {
      if (!user) return;
      const snoozeUntil = new Date(Date.now() + weeks * 7 * 24 * 3600 * 1000).toISOString();
      const keep = (c: FeedCard): boolean => {
        if (c.type === 'game' || c.type === 'tip' || c.type === 'milestone') {
          if (c.data.id !== contentId) return true;
          if (!childId) return false;
          return c.data._resolvedChildId !== childId;
        }
        return true;
      };
      setCards((prev) => prev.filter(keep));
      queryClient.setQueryData<FeedCard[]>(feedCacheKey(user.id), (prev) =>
        prev ? prev.filter(keep) : prev,
      );
      void supabase
        .from('card_snoozes')
        .upsert(
          { user_id: user.id, content_id: contentId, child_id: childId, snooze_until: snoozeUntil },
          { onConflict: 'user_id,content_id,child_id' },
        )
        .then(() => {});
      void queryClient.invalidateQueries({ queryKey: ['card-snoozes', user.id] });
    },
    [user],
  );

  // ── Pinned Mori-message lifecycle ─────────────────────────────────────────
  // read_at / dismissed_at on user_card_instances drive whether the card
  // reappears on the next session. Events fire with instanceId so the
  // content_interactions log can distinguish template vs instance shows.

  const markPinnedRead = useCallback(
    (instanceId: string, contentLibraryId: string) => {
      if (!user) return;
      const keep = (c: FeedCard): boolean =>
        c.type !== 'mori_message' || c.data.instanceId !== instanceId;
      setCards((prev) => prev.filter(keep));
      queryClient.setQueryData<FeedCard[]>(feedCacheKey(user.id), (prev) =>
        prev ? prev.filter(keep) : prev,
      );
      void supabase
        .from('user_card_instances')
        .update({ read_at: new Date().toISOString() })
        .eq('id', instanceId)
        .then(() => {});
      const cardType = pinnedCardsRef.current.find(
        (c) => c.type === 'mori_message' && c.data.instanceId === instanceId,
      )?.type === 'mori_message'
        ? 'mori_message'
        : 'unknown';
      fireEvent('card.tapped', {
        userId: user.id,
        contentLibraryId,
        cardType,
        ctaAction: 'mori_message',
      });
    },
    [user],
  );

  const dismissPinnedInstance = useCallback(
    (instanceId: string, contentLibraryId: string) => {
      if (!user) return;
      const keep = (c: FeedCard): boolean =>
        c.type !== 'mori_message' || c.data.instanceId !== instanceId;
      setCards((prev) => prev.filter(keep));
      queryClient.setQueryData<FeedCard[]>(feedCacheKey(user.id), (prev) =>
        prev ? prev.filter(keep) : prev,
      );
      void supabase
        .from('user_card_instances')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', instanceId)
        .then(() => {});
      fireEvent('card.dismissed', {
        userId: user.id,
        contentLibraryId,
        cardType: 'mori_message',
        dismissedFor: 'forever',
      });
    },
    [user],
  );

  return {
    cards,
    isLoading,
    isLoadingMore,
    hasMore,
    trackCardInteraction,
    dismissCard,
    snoozeCard,
    loadMore,
    markPinnedRead,
    dismissPinnedInstance,
  };
}
